import { useEffect, useState } from 'react';
import { uiText } from '../../i18n';
import { reduced } from '../../ui/motion';
import { StudioIcon } from '../studio/StudioIcon';
import { mmss } from './reels-policy';

/** Ритм примера — от типа шаблона (APP-SPEC-5 §8.2): нейросъёмка — быстрые склейки, вау-эффект — один долгий план,
    оживление — мягкие наплывы; у набора и пакета — по кадру на шаблон. */
const slotOf = (type: string, deck: boolean) => (deck ? 1300 : ({ n: 1100, e: 2800, o: 2200 } as Record<string, number>)[type] ?? 2200);

export interface HeroFrame {
  url: string;
  label?: string;
  /** Ключ кадра, как у эталона (data-k): `d<шаблон><N>`, `dapp<стиль>_<N>`, `m<шаблон>` */
  key?: string;
  /** Положение кадра в рамке (object-position): у сцены — проход по обложке слева направо */
  pos?: string;
}
/** Пример в рамке 9:16: кадры сменяют друг друга наплывом, полоски показывают, сколько кадров и где сейчас;
    время, бейдж, ярлык «Пример» (у набора — название шаблона кадра). «Уменьшить движение» — кадр стоит. */
export function ReelsHero({
  frames,
  type,
  durationS,
  badge,
  deck = false,
  ghosts = [],
}: {
  frames: HeroFrame[];
  type: string;
  durationS: number | null;
  badge: 'hit' | 'new' | null;
  deck?: boolean;
  ghosts?: string[];
}) {
  const [tick, setTick] = useState(0);
  // Прежний кадр гаснет под новым только после первой смены: на открытии (и в заморозке .still) стоит один кадр.
  const moved = tick > 0;
  const slot = slotOf(type, deck);
  const still = reduced();
  const count = frames.length;
  const index = count ? tick % count : 0;
  useEffect(() => {
    if (still || count < 2) return;
    const timer = window.setTimeout(() => setTick(value => value + 1), slot + 900);
    return () => clearTimeout(timer);
  }, [tick, count, slot, still]);
  const current = frames[index % Math.max(1, count)];
  return (
    <div className={`rl-hero${deck ? ' deck' : ''}`} style={{ ['--fw' as string]: '151px', ['--fh' as string]: '268px' }}>
      {deck && ghosts.length > 0 && (
        <span className="rl-ghs" aria-hidden="true">
          {ghosts.slice(0, 2).map((url, i) => (
            <span key={url + i} className={`rl-gh ${i ? 'r' : 'l'}`}>
              <img src={url} alt="" />
            </span>
          ))}
        </span>
      )}
      <div className="rl-f" data-ar="9:16" data-mo={still ? undefined : type === 'n' ? 'dyn' : 'kb'} style={{ width: 151, height: 268, ['--sl' as string]: `${slot}ms` }}>
        <span className="rl-ss">
          {frames.map((frame, i) => (
            <img
              key={(frame.key ?? frame.url) + i}
              src={frame.url}
              alt=""
              draggable={false}
              data-k={frame.key}
              data-v={i % 4}
              style={frame.pos ? { objectPosition: frame.pos } : undefined}
              className={i === index ? 'on' : moved && count > 1 && i === (index - 1 + count) % count ? 'was' : undefined}
            />
          ))}
        </span>
        {count > 1 && (
          <span className="rl-bars" aria-hidden="true">
            {frames.map((frame, i) => (
              <i key={(frame.key ?? frame.url) + i} className={i < index ? 'done' : i === index ? 'now' : undefined}>
                <b />
              </i>
            ))}
          </span>
        )}
        {durationS !== null && (
          <span className="rl-vt">
            <StudioIcon kind="play" />
            <b className="mono">{mmss(durationS)}</b>
          </span>
        )}
        {badge && <span className="rl-hb">{uiText(badge === 'hit' ? 'Хит' : 'Новое')}</span>}
        {(count > 0 || deck) && <span className="rl-tag">{uiText(deck ? (current?.label ?? 'Пример') : 'Пример')}</span>}
        {!count && (
          <span className="rl-ss rl-empty" aria-hidden="true">
            <StudioIcon kind="film" />
          </span>
        )}
      </div>
    </div>
  );
}
